import React from 'react';
import { Flame, Clock, AlertTriangle, Leaf, Wind } from 'lucide-react';
import TimerWidget from './TimerWidget';

const toText = (v) => {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') return v.tip || v.text || v.content || v.optimization || v.suggestion || '';
  return String(v ?? '');
};

// Renders a gi_optimization item regardless of shape:
// string | {note, ingredient, substitution} | {tip} | etc.
const OptimizationItem = ({ opt }) => {
  if (typeof opt === 'string') return <span>{opt}</span>;
  if (opt && typeof opt === 'object') {
    const { note, ingredient, substitution, tip, text, content } = opt;
    if (substitution || ingredient) {
      return (
        <span className="flex flex-col gap-0.5">
          {ingredient && (
            <span>
              <span className="line-through text-green-400">{ingredient}</span>
              {substitution && (
                <>
                  <span className="mx-1.5 text-green-400">→</span>
                  <span className="font-medium text-green-900">{substitution}</span>
                </>
              )}
            </span>
          )}
          {!ingredient && substitution && <span className="font-medium text-green-900">{substitution}</span>}
          {note && <span className="text-xs text-green-600 italic">{note}</span>}
        </span>
      );
    }
    return <span>{tip || text || content || toText(opt)}</span>;
  }
  return <span>{toText(opt)}</span>;
};

const StatCard = ({ icon: Icon, label, value, unit, colorClass }) => (
  <div className={`rounded-2xl p-4 flex flex-col items-center gap-1 ${colorClass}`}>
    <Icon className="w-6 h-6 mb-1" />
    <span className="text-2xl font-bold">{value}</span>
    <span className="text-xs opacity-70">{unit}</span>
    <span className="text-xs font-medium opacity-80 text-center">{label}</span>
  </div>
);

const AirfryerPanel = ({ config }) => {
  if (!config) return null;

  const steps = Array.isArray(config.steps) ? config.steps : [];
  const mainStep = steps.find(s => s.config?.time) || null;
  const displayTime = mainStep?.config?.time || config.time;
  const displayTemp = mainStep?.config?.temp || config.temp;

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {displayTemp && (
          <StatCard
            icon={Flame}
            label="Température"
            value={displayTemp}
            unit="°C"
            colorClass="bg-orange-50 text-orange-600"
          />
        )}
        {config.preheat_temp && config.preheat_temp !== displayTemp && (
          <StatCard
            icon={Flame}
            label="Préchauffage"
            value={config.preheat_temp}
            unit="°C"
            colorClass="bg-red-50 text-red-600"
          />
        )}
        {displayTime && (
          <StatCard
            icon={Clock}
            label="Durée"
            value={displayTime}
            unit="min"
            colorClass="bg-blue-50 text-blue-600"
          />
        )}
      </div>

      {/* Shaking reminder */}
      {config.shaking_needed && (
        <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            Secouer le panier
            {config.shaking_interval_minutes ? ` toutes les ${config.shaking_interval_minutes} min` : ''}
          </p>
        </div>
      )}

      {/* Timer */}
      {displayTime && (
        <div className="flex justify-center">
          <TimerWidget minutes={displayTime} label="Minuteur Airfryer" appliance="airfryer" />
        </div>
      )}

      {/* Steps list (if provided) */}
      {steps.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
          <h3 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
            <Wind className="w-4 h-4 text-orange-400" /> Étapes
          </h3>
          {steps.map((step, i) => (
            <div key={step.id || i} className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              <p className="text-sm text-gray-700 leading-relaxed">{step.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Instructions text fallback */}
      {!steps.length && config.instructions && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-700 mb-3 text-sm flex items-center gap-2">
            <Wind className="w-4 h-4 text-orange-400" /> Préparation Airfryer
          </h3>
          <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {config.instructions}
          </div>
        </div>
      )}

      {/* GI optimizations */}
      {Array.isArray(config.gi_optimizations) && config.gi_optimizations.length > 0 && (
        <div className="bg-green-50 rounded-2xl border border-green-100 p-5">
          <h3 className="font-semibold text-green-800 mb-3 text-sm flex items-center gap-2">
            <Leaf className="w-4 h-4" /> Optimisations IG bas
          </h3>
          <ul className="space-y-2">
            {config.gi_optimizations.map((opt, i) => (
              <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                <OptimizationItem opt={opt} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AirfryerPanel;
